import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, MessageCircle, CalendarCheck, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { track, whatsappLink } from "@/lib/analytics";
import { useContent } from "@/App";

const LINKS = [
  { label: "Villa", hash: "villa" },
  { label: "Stay", hash: "rooms" },
  { label: "Gallery", hash: "gallery" },
  { label: "Karwar", hash: "karwar" },
  { label: "FAQ", hash: "faq" },
  { label: "Journal", path: "/blog" },
  { label: "Contact", hash: "contact" },
];

export function NavLink({ link, onClick, className }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const go = (e) => {
    if (link.path) return;
    e.preventDefault();
    onClick?.();
    if (pathname !== "/") navigate(`/#${link.hash}`);
    else document.getElementById(link.hash)?.scrollIntoView({ behavior: "smooth" });
  };
  if (link.path) return <Link to={link.path} className={className} onClick={onClick} data-testid={`nav-${link.label.toLowerCase()}-link`}>{link.label}</Link>;
  return <a href={`/#${link.hash}`} onClick={go} className={className} data-testid={`nav-${link.label.toLowerCase()}-link`}>{link.label}</a>;
}

export function BookButton({ className = "", children, testid = "nav-check-availability-btn" }) {
  const go = () => {
    track("booking_cta_click", { source: testid });
    const el = document.getElementById("availability-widget");
    if (el && window.location.pathname === "/") el.scrollIntoView({ behavior: "smooth", block: "center" });
    else window.location.href = "/#availability-widget";
  };
  return (
    <button onClick={go} data-testid={testid} className={`group inline-flex items-center gap-2 rounded-full bg-copper px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-charcoal transition-colors duration-300 hover:bg-copper-300 ${className}`}>
      <CalendarCheck className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
      {children || "Check Availability"}
    </button>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const content = useContent();
  const phone = content?.settings?.phone;

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <>
      <header className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled ? "glass py-3" : "bg-transparent py-5"}`} data-testid="main-nav">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" aria-label="Mystique Copper Villa home">
            <img src="/logo.png" alt="Mystique Copper Villa emblem" className="h-10 w-10 rounded-full border border-copper/40 object-cover" />
            <span className="font-serif text-xl text-cream hidden sm:inline">Mystique <span className="text-copper-gradient italic">Copper</span> Villa</span>
          </Link>
          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {LINKS.map((l) => (
              <NavLink key={l.label} link={l} className="text-sm font-light text-cream-muted transition-colors duration-300 hover:text-copper-300" />
            ))}
          </nav>
          <div className="hidden items-center gap-4 lg:flex">
            {phone && (
              <a href={`tel:${phone}`} onClick={() => track("phone_click", { source: "nav" })} data-testid="nav-call-btn" className="flex items-center gap-2 text-sm text-cream-muted transition-colors hover:text-copper-300" aria-label="Call the villa">
                <Phone className="h-4 w-4" /> Call
              </a>
            )}
            <BookButton />
          </div>
          <button className="lg:hidden text-cream" onClick={() => setOpen(true)} data-testid="nav-menu-open-btn" aria-label="Open menu">
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex flex-col bg-charcoal/95 backdrop-blur-xl" data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <span className="flex items-center gap-3">
                <img src="/logo.png" alt="" className="h-10 w-10 rounded-full border border-copper/40 object-cover" />
                <span className="font-serif text-xl">Mystique <span className="text-copper-gradient italic">Copper</span> Villa</span>
              </span>
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close-btn" aria-label="Close menu"><X className="h-6 w-6 text-cream" /></button>
            </div>
            <nav className="flex flex-1 flex-col items-start justify-center gap-6 px-8" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.div key={l.label} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 * i }}>
                  <NavLink link={l} onClick={() => setOpen(false)} className="font-serif text-4xl text-cream transition-colors hover:text-copper-300" />
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function MobileBottomBar() {
  const content = useContent();
  const wa = content?.settings?.whatsapp_number;
  const waHref = wa ? whatsappLink(wa, "Hi! I'd like to enquire about Mystique Copper Villa, Karwar.") : "/#contact";
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-2 border-t border-copper/25 bg-charcoal/90 backdrop-blur-xl lg:hidden" data-testid="mobile-bottom-bar">
      <a
        href="/#availability-widget"
        onClick={(e) => {
          e.preventDefault();
          track("booking_cta_click", { source: "mobile-bottom-bar" });
          if (window.location.pathname === "/") document.getElementById("availability-widget")?.scrollIntoView({ behavior: "smooth", block: "center" });
          else window.location.href = "/#availability-widget";
        }}
        data-testid="mobile-bottom-check-availability-btn"
        className="flex items-center justify-center gap-2 bg-copper py-4 text-xs font-mono uppercase tracking-[0.15em] text-charcoal"
      >
        <CalendarCheck className="h-4 w-4" /> Check Availability
      </a>
      <a
        href={waHref}
        target={wa ? "_blank" : undefined}
        rel="noreferrer"
        onClick={() => track("whatsapp_click", { source: "mobile-bottom-bar" })}
        data-testid="mobile-bottom-whatsapp-btn"
        className="flex items-center justify-center gap-2 py-4 text-xs font-mono uppercase tracking-[0.15em] text-cream"
      >
        <MessageCircle className="h-4 w-4 text-copper-300" /> WhatsApp
      </a>
    </div>
  );
}
